import React, { useCallback, useState } from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';
import _ from 'lodash';
import i18n from '../i18n';
import GlobalNotification from './common/snackbar';
import AvatarCard from './avatar/avatar-card';
import apiManage from '../request';
import {
    buildRestaurantStaticUploadQueryFileName,
    extractFileNameForRestaurantStaticDelete,
} from '../utils/restaurant-static-image-url';
import { MAX_IMAGE_UPLOAD_BYTES } from '../constants/base.contants';
import {
    IMAGE_UPLOAD_SPECS,
    type ImageUploadSpec,
} from '../utils/image-upload-spec';

export type RestaurantStaticImageUploadKind = 'menu-image' | 'dish-image' | 'qop-logo-image';

export interface RestaurantStaticImageUploadProps {
    /** 餐厅 string_id */
    stringId: string;
    kind: RestaurantStaticImageUploadKind;
    /**
     * Apifox：query `file_name` 为 `{id}.{后缀}`，此处传一级菜单 `main_group_id` 或菜品 `item_id`；未落库前为 null 则无法上传。
     * `qop-logo-image` 不依赖实体 id，可省略。
     */
    uploadEntityId?: number | null;
    /** 实体字段原始值（文件名或 URL） */
    imageRef: string | null | undefined;
    /** 已解析的可展示 URL */
    displayUrl: string;
    /** 上传成功且服务端已落库文件名/URL 后调用（父组件负责 PATCH 实体）；`deferStaticUpload` 时可为空 */
    onCommittedFileName?: (fileName: string) => void | Promise<void>;
    /** 删除静态文件成功后调用（父组件负责清空实体字段）；`deferStaticUpload` 时可为空 */
    onRemoved?: () => void | Promise<void>;
    /**
     * `dish-image` / `menu-image`：为 true 时不立即请求 restaurant-static，将文件交给 `onStagedStaticFile`，删除交给 `onDeferredStaticRemove`，由父级在保存时统一调接口。
     */
    deferStaticUpload?: boolean;
    /** 与 `deferStaticUpload` 配套：用户选图裁剪后暂存本地 `File`（可异步，组件会展示 loading 至完成） */
    onStagedStaticFile?: (file: File) => void | Promise<void>;
    /** 与 `deferStaticUpload` 配套：用户点删除时仅通知父级（可异步，组件会展示 loading 至完成） */
    onDeferredStaticRemove?: () => void | Promise<void>;
    disabled?: boolean;
    /**
     * 与 `AvatarCard` 头像区域边长（px），默认 140。
     * 菜单详情/新建菜品可分别传 108、145 等与版心一致。
     */
    avatarSize?: number;
    /**
     * 空态样式：传 `firstScreenGallery` 时对齐首页与营销 Figma 1838:52035 大图位（虚线框 + 加号 +「添加图片」）。
     * 默认 `legacy` 为原 `AvatarCard` 上传方块。
     */
    uploadEmptyVariant?: 'legacy' | 'firstScreenGallery';
    /** 与 `firstScreenGallery` 配套：槽位宽（px），默认 256 */
    slotWidth?: number;
    /** 与 `firstScreenGallery` 配套：槽位高（px），默认 160 */
    slotHeight?: number;
    /** 预览用大图 URL；不传时与 `displayUrl` 相同（菜单图等单 URL 场景） */
    displayUrlMax?: string;
    /** 展示在 `AvatarCard` 下方的名称（如推荐菜名） */
    avatarLabel?: string;
    /** 首页营销大图位空态主文案；不传则用 `first_screen_text_add_image` */
    emptyUploadLabel?: string;
    /** `firstScreenGallery` 空槽点击回调（不打开选图，如推荐菜「添加菜品」） */
    onGalleryEmptyClick?: () => void;
    /**
     * `avatarLabel` 的排版：`default` 居中省略；`firstScreenSlot` 为首页与营销基础设置等图位说明（body2、次要色、左齐）。
     */
    avatarCaptionLayout?: 'default' | 'firstScreenSlot';
    /** 当前静态图片入口的裁剪与建议输出规格。 */
    uploadSpec?: ImageUploadSpec;
    /** @deprecated 已改用 AvatarCard，占位由 AvatarCard 空态展示，传参无效 */
    emptyPlaceholder?: React.ReactNode;
    sx?: SxProps<Theme>;
}

/**
 * 餐厅静态资源图：基于 `AvatarCard`（统一选图+裁切）后上传 `restaurant-static` 接口。
 * 菜单/菜品默认裁切 16:10；`qop-logo-image` 默认 1:1；`stringId` 透传供网络搜图。
 */
export function RestaurantStaticImageUpload(props: RestaurantStaticImageUploadProps): React.ReactElement {
    const {
        stringId,
        kind,
        uploadEntityId = null,
        imageRef,
        displayUrl,
        onCommittedFileName,
        onRemoved,
        deferStaticUpload = false,
        onStagedStaticFile,
        onDeferredStaticRemove,
        disabled,
        avatarSize,
        uploadEmptyVariant = 'legacy',
        slotWidth,
        slotHeight,
        displayUrlMax,
        avatarLabel,
        emptyUploadLabel,
        onGalleryEmptyClick,
        avatarCaptionLayout = 'default',
        uploadSpec = kind === 'qop-logo-image' ? IMAGE_UPLOAD_SPECS.square : IMAGE_UPLOAD_SPECS.landscape,
        sx,
    } = props;
    const [busy, setBusy] = useState(false);

    // 是否已有可展示/可删除的图片
    const hasImage = Boolean((imageRef ?? '').trim() || (displayUrl ?? '').trim());
    // Logo 上传不依赖菜单/菜品实体 id
    const isQopLogo = kind === 'qop-logo-image';

    const runUpload = useCallback(
        async (file: File) => {
            if (!file.type.startsWith('image/')) {
                GlobalNotification.error(i18n.t('general_message_image_type_incorrect_upload_failed'));
                return;
            }
            // 记录最终 File 大小，便于在浏览器控制台核对实际上传内容。
            console.info('[image-upload] final file', {
                name: file.name,
                type: file.type,
                sizeBytes: file.size,
                sizeKb: Number((file.size / 1024).toFixed(2)),
            });
            if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
                GlobalNotification.error(
                    i18n.t('input_rule_text_please_enter_file_size_cannot_exceed_x', {
                        x: `${MAX_IMAGE_UPLOAD_BYTES / 1024}k`,
                    }),
                );
                return;
            }
            if (deferStaticUpload && (kind === 'dish-image' || kind === 'menu-image')) {
                setBusy(true);
                try {
                    await Promise.resolve(onStagedStaticFile?.(file));
                } finally {
                    setBusy(false);
                }
                return;
            }
            if (isQopLogo) {
                setBusy(true);
                try {
                    const res = await apiManage.post_restaurant_static_qop_logo_image(stringId, file);
                    if (_.get(res, 'error')) {
                        GlobalNotification.error(i18n.t('general_text_failed_upload_picture'));
                        return;
                    }
                    // 响应可能无文件名；固定回填约定展示名，供父级刷新缓存破坏参数
                    const fn = apiManage.parseRestaurantStaticUploadFileName(res) || 'logo.jpg';
                    await onCommittedFileName?.(fn);
                } catch (e) {
                    console.error('RestaurantStaticImageUpload', e);
                    GlobalNotification.error(i18n.t('general_text_failed_upload_picture'));
                } finally {
                    setBusy(false);
                }
                return;
            }
            if (uploadEntityId == null || !Number.isFinite(uploadEntityId) || uploadEntityId <= 0) {
                GlobalNotification.error(i18n.t('menu_library_static_image_need_entity_id'));
                return;
            }
            const queryFileName = buildRestaurantStaticUploadQueryFileName(uploadEntityId, file);
            setBusy(true);
            try {
                const res =
                    kind === 'menu-image'
                        ? await apiManage.post_restaurant_static_menu_image(stringId, file, queryFileName)
                        : await apiManage.post_restaurant_static_dish_image(stringId, file, queryFileName);
                if (_.get(res, 'error')) {
                    GlobalNotification.error(i18n.t('general_text_failed_upload_picture'));
                    return;
                }
                const fn = apiManage.parseRestaurantStaticUploadFileName(res);
                if (!fn) {
                    GlobalNotification.error(i18n.t('general_text_failed_upload_picture'));
                    return;
                }
                await onCommittedFileName?.(fn);
            } catch (e) {
                console.error('RestaurantStaticImageUpload', e);
                GlobalNotification.error(i18n.t('general_text_failed_upload_picture'));
            } finally {
                setBusy(false);
            }
        },
        [deferStaticUpload, isQopLogo, kind, onCommittedFileName, onStagedStaticFile, stringId, uploadEntityId],
    );

    const handleAvatarChange = useCallback(
        (_avatarUrl: string, file: File) => {
            void runUpload(file);
        },
        [runUpload],
    );

    const runDelete = useCallback(async () => {
        if (deferStaticUpload && (kind === 'dish-image' || kind === 'menu-image')) {
            setBusy(true);
            try {
                await Promise.resolve(onDeferredStaticRemove?.());
            } finally {
                setBusy(false);
            }
            return;
        }
        if (isQopLogo) {
            setBusy(true);
            try {
                const res = await apiManage.delete_restaurant_static_qop_logo_image(stringId);
                if (_.get(res, 'error')) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
                await onRemoved?.();
            } catch (e) {
                console.error('RestaurantStaticImageUpload delete', e);
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            } finally {
                setBusy(false);
            }
            return;
        }
        const fn = extractFileNameForRestaurantStaticDelete(imageRef);
        if (!fn) {
            await onRemoved?.();
            return;
        }
        setBusy(true);
        try {
            const res =
                kind === 'menu-image'
                    ? await apiManage.delete_restaurant_static_menu_image(stringId, fn)
                    : await apiManage.delete_restaurant_static_dish_image(stringId, fn);
            if (_.get(res, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            }
            await onRemoved?.();
        } catch (e) {
            console.error('RestaurantStaticImageUpload delete', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            setBusy(false);
        }
    }, [deferStaticUpload, imageRef, isQopLogo, kind, onDeferredStaticRemove, onRemoved, stringId]);

    const uploadDisabled =
        Boolean(disabled) ||
        busy ||
        (!isQopLogo &&
            !deferStaticUpload &&
            (uploadEntityId == null || !Number.isFinite(uploadEntityId) || uploadEntityId <= 0));

    const url = (displayUrl ?? '').trim() || undefined;
    const urlMax = (displayUrlMax ?? displayUrl ?? '').trim() || undefined;
    // 选图弹窗标题：菜单 / 菜品 / Logo
    const pickerTitle =
        kind === 'menu-image'
            ? i18n.t('image_source_picker_text_title_menu')
            : kind === 'qop-logo-image'
              ? i18n.t('image_source_picker_text_title_logo')
              : i18n.t('image_source_picker_text_title_dish');

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                minHeight: 0,
                display: 'flex',
                alignItems: avatarCaptionLayout === 'firstScreenSlot' && avatarLabel ? 'flex-start' : 'center',
                justifyContent:
                    avatarCaptionLayout === 'firstScreenSlot' && avatarLabel ? 'flex-start' : 'center',
                ...sx,
            }}
        >
            <AvatarCard
                name={avatarLabel}
                avatarUrl={url}
                avatarUrlMax={urlMax ?? url}
                onChangeAvatarUrl={uploadDisabled ? undefined : handleAvatarChange}
                onDelete={hasImage && !disabled && !busy ? () => void runDelete() : undefined}
                isDisabledUpload={uploadDisabled}
                size={avatarSize ?? 140}
                uploadEmptyVariant={uploadEmptyVariant}
                slotWidth={slotWidth}
                slotHeight={slotHeight}
                slotCaptionLayout={avatarCaptionLayout}
                busy={busy}
                emptyUploadLabel={emptyUploadLabel}
                onGalleryEmptyClick={onGalleryEmptyClick}
                stringId={stringId}
                uploadSpec={uploadSpec}
                pickerTitle={pickerTitle}
            />
        </Box>
    );
}
